/**
 * Device-local placement of the main sidebar collapse button.
 * Applied as CSS variables on documentElement so the button can paint
 * before React reads the preference.
 */

import { useSyncExternalStore } from 'react';

export const SIDEBAR_TOGGLE_POSITION_STORAGE_KEY = 'adieuu:sidebar-toggle-position-v1';
export const SIDEBAR_TOGGLE_ANCHOR_VAR = '--sidebar-toggle-anchor';
export const SIDEBAR_TOGGLE_OFFSET_VAR = '--sidebar-toggle-offset';
export const SIDEBAR_TOGGLE_TRANSFORM_VAR = '--sidebar-toggle-transform';

export type SidebarTogglePosition = 'top' | 'center' | 'bottom';

export interface SidebarTogglePreference {
  position: SidebarTogglePosition;
  offsetPx: number;
}

export const DEFAULT_SIDEBAR_TOGGLE_PREFERENCE: SidebarTogglePreference = {
  position: 'center',
  offsetPx: 0,
};

const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function isSidebarTogglePosition(value: unknown): value is SidebarTogglePosition {
  return value === 'top' || value === 'center' || value === 'bottom';
}

/** Finite integers only. Non-numeric and non-integer values are rejected. */
export function parseSidebarToggleOffset(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isInteger(value) && Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) {
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : null;
  }
  return null;
}

export function parseSidebarTogglePreference(raw: string | null): SidebarTogglePreference {
  if (!raw) return DEFAULT_SIDEBAR_TOGGLE_PREFERENCE;
  try {
    const parsed = JSON.parse(raw) as { position?: unknown; offsetPx?: unknown };
    const position = isSidebarTogglePosition(parsed.position)
      ? parsed.position
      : DEFAULT_SIDEBAR_TOGGLE_PREFERENCE.position;
    const offsetPx = parseSidebarToggleOffset(parsed.offsetPx);
    return {
      position,
      offsetPx: offsetPx ?? DEFAULT_SIDEBAR_TOGGLE_PREFERENCE.offsetPx,
    };
  } catch {
    return DEFAULT_SIDEBAR_TOGGLE_PREFERENCE;
  }
}

export function sidebarToggleCssVars(pref: SidebarTogglePreference): {
  anchor: string;
  offset: string;
  transform: string;
} {
  const offset = `${pref.offsetPx}px`;
  switch (pref.position) {
    case 'top':
      return { anchor: '0px', offset, transform: 'none' };
    case 'bottom':
      return { anchor: '100%', offset, transform: 'translateY(-100%)' };
    default:
      return { anchor: '50%', offset, transform: 'translateY(-50%)' };
  }
}

export function applySidebarToggleCssVars(pref: SidebarTogglePreference): void {
  if (typeof document === 'undefined') return;
  const vars = sidebarToggleCssVars(pref);
  const root = document.documentElement;
  root.style.setProperty(SIDEBAR_TOGGLE_ANCHOR_VAR, vars.anchor);
  root.style.setProperty(SIDEBAR_TOGGLE_OFFSET_VAR, vars.offset);
  root.style.setProperty(SIDEBAR_TOGGLE_TRANSFORM_VAR, vars.transform);
}

export function readSidebarTogglePreference(): SidebarTogglePreference {
  if (typeof localStorage === 'undefined') return DEFAULT_SIDEBAR_TOGGLE_PREFERENCE;
  try {
    return parseSidebarTogglePreference(localStorage.getItem(SIDEBAR_TOGGLE_POSITION_STORAGE_KEY));
  } catch {
    return DEFAULT_SIDEBAR_TOGGLE_PREFERENCE;
  }
}

export function writeSidebarTogglePreference(
  next: Partial<SidebarTogglePreference>,
): SidebarTogglePreference | null {
  const current = readSidebarTogglePreference();
  const position = next.position ?? current.position;
  const offsetPx = next.offsetPx ?? current.offsetPx;
  if (!isSidebarTogglePosition(position)) return null;
  if (parseSidebarToggleOffset(offsetPx) === null) return null;
  const pref: SidebarTogglePreference = { position, offsetPx };
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(SIDEBAR_TOGGLE_POSITION_STORAGE_KEY, JSON.stringify(pref));
    } catch {
      return null;
    }
  }
  applySidebarToggleCssVars(pref);
  cached = pref;
  emit();
  return pref;
}

let cached: SidebarTogglePreference = readSidebarTogglePreference();

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot(): SidebarTogglePreference {
  const next = readSidebarTogglePreference();
  if (cached.position === next.position && cached.offsetPx === next.offsetPx) return cached;
  cached = next;
  return cached;
}

export function useSidebarTogglePreference(): SidebarTogglePreference {
  return useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_SIDEBAR_TOGGLE_PREFERENCE);
}

if (typeof document !== 'undefined') {
  applySidebarToggleCssVars(readSidebarTogglePreference());
}
